/* @HEADER
 * @version 1.0.1526 | 2026-09-18
 * @purpose The host's entry state before authentication — the unauth route resolved from hash + pathname + search and re-resolved on hashchange, and the synchronous «is the visitor authenticated» read the landing gate needs; moved out of App.tsx in campaign 3 (ZVX-DEV-6084).
 * @sidecar useUnauthRouteState.ts.header.md
 * @layer app | @hex adapter | @ctx auth
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6084, ZVX-DEV-2412, ZVX-DEV-2376

import { useEffect, useState } from 'react';
import { AUTH_STORAGE_KEYS } from '../features/auth/verifyEmailApi';
import { resolveUnauthRoute } from './unauthRouteResolve';
import type { UnauthRouteState } from './unauthRouteResolve';
// @ts-expect-error — framework-free .mjs decision, typed by its JSDoc
import { visitorAuthenticatedFrom } from './unauthEntryDecisions.mjs';

/**
 * Resolve "is the visitor authenticated?" without a React hook so it can be
 * called in the synchronous App() render phase before any providers mount.
 * The result only changes on page reload (token writes → hashchange →
 * remount), which is sufficient for the gate. ZVX-DEV-2376.
 */
export function isVisitorAuthenticated(): boolean {
  const getItem =
    typeof localStorage === 'undefined' ? null : (k: string) => localStorage.getItem(k);
  return visitorAuthenticatedFrom(getItem, AUTH_STORAGE_KEYS);
}

/**
 * Unauthenticated screens for Wave 5 (ZVX-DEV-1344, ZVX-DEV-1353, ZVX-DEV-1346,
 * ZVX-DEV-1351) are reachable via URL hash (`#register`, `#check-email?email=`,
 * `#verify-email?token=`, `#login[?next=]`, `#forgot-password`,
 * `#reset-password?token=`) — and, since ZVX-DEV-2412, via the PLAIN-PATH links
 * the API server actually emails (`/verify-email?token=…`, `/reset-password?token=…`).
 * The resolution itself is the pure, unit-tested `resolveUnauthRoute`
 * (hooks/unauthRouteResolve.ts): hash FIRST (in-app CTA navigation), then the
 * real pathname + search. This hook only keeps the hash live across
 * `hashchange`. Documented in features/auth/README.md.
 */
export function useUnauthRouteState(): UnauthRouteState {
  const [hash, setHash] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.hash : '',
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return resolveUnauthRoute(hash, pathname, search);
}
